import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Folder } from '../../../schemas/folder.schema';
import { useFolderRepository } from '../../rpc';

/**
 * Размер СТРАНИЦЫ дозагрузки папок (не потолок всего Диска). Дерево, крошки и
 * расчёт прав зоны (подъём по `access_root_id`) требуют предков, которых одна
 * страница не гарантирует, поэтому хук читает страницы в цикле до конца —
 * обрезка первой тысячей теряла папки на больших инсталляциях.
 */
export const FOLDERS_LIMIT = 1000;

/**
 * Предохранитель от бесконечной дозагрузки (например, при сбое пагинации на
 * сервере): 50 страниц × FOLDERS_LIMIT = 50 000 папок — на порядок больше
 * реальных инсталляций. Если Диск дорос до потолка, правильный ответ — ленивое
 * поддерево по разворачиванию узла, а не рост константы.
 */
export const MAX_FOLDER_PAGES = 50;

type FolderRepositoryClient = ReturnType<typeof useFolderRepository>;

/**
 * Читает ВСЕ видимые папки страницами по `FOLDERS_LIMIT`.
 *
 * Явный `orderBy('id', 'asc')` обязателен: без стабильной сортировки порядок
 * строк между страницами недетерминирован — страницы пересекались бы и теряли
 * папки. Каждая страница идёт той же цепочкой `withMyAclLevel()`, чтобы у всех
 * строк была виртуальная колонка ACL-уровня.
 */
/**
 * Читает папки ОБЛАСТИ по якорю: сервер отдаёт id корня, предков и видимого
 * поддерева (`subtreeIdsByCode`), строки дочитываются той же цепочкой с
 * ACL-колонкой и штатной видимостью. Для встраиваемой панели folder mode —
 * вместо загрузки всего Диска (план drive_folder_lifecycle, этап 4).
 */
async function fetchScopeFolders(
  folderRepo: FolderRepositoryClient,
  scopeCode: string
): Promise<Folder[]> {
  const scope = await folderRepo.subtreeIdsByCode(scopeCode);
  if (!scope.rootId || scope.ids.length === 0) return [];
  const all: Folder[] = [];
  for (let offset = 0; offset < scope.ids.length; offset += FOLDERS_LIMIT) {
    const chunk = scope.ids.slice(offset, offset + FOLDERS_LIMIT);
    const rows = await folderRepo
      .withMyAclLevel()
      .whereIn('id', chunk)
      .orderBy('id', 'asc')
      .fetch();
    all.push(...(rows ?? []));
  }
  return all;
}

async function fetchAllFolders(folderRepo: FolderRepositoryClient): Promise<Folder[]> {
  const all: Folder[] = [];
  for (let page = 0; page < MAX_FOLDER_PAGES; page++) {
    // withMyAclLevel — мой ACL-уровень в custom-зонах (точный гейтинг кнопок)
    const rows = await folderRepo
      .withMyAclLevel()
      .orderBy('id', 'asc')
      .offset(page * FOLDERS_LIMIT)
      .limit(FOLDERS_LIMIT)
      .fetch();
    const chunk = rows ?? [];
    all.push(...chunk);
    if (chunk.length < FOLDERS_LIMIT) return all;
  }
  logger.warn(
    `[useDriveFolders] Достигнут потолок ${MAX_FOLDER_PAGES} страниц по ${FOLDERS_LIMIT} папок — дерево может быть неполным`
  );
  return all;
}

export interface UseDriveFoldersResult {
  /** Плоский список ВИДИМЫХ папок (сервер уже отфильтровал чужие private-зоны). */
  folders: Folder[];
  /** Те же папки по id — для путей, прав и цвета иконок. */
  foldersById: Map<number, Folder>;
  /** Корень Диска — единственная папка без родителя (COMPANY_DRIVE). */
  rootFolder: Folder | undefined;
  loading: boolean;
  error: string | null;
  /** Перечитать папки: дерево не видит чужих изменений до явного refresh. */
  reload: () => Promise<void>;
}

/**
 * useDriveFolders — загрузка папок Диска и производные от неё структуры.
 *
 * Единый источник для всех экранов Диска: страницы, встраиваемой панели,
 * файл-пикера и стенда. Раньше этот блок (запрос + `foldersById` + состояние
 * загрузки/ошибки) был скопирован в каждом из них.
 *
 * Папки грузятся плоским списком ЦЕЛИКОМ — страницами по `FOLDERS_LIMIT` до
 * исчерпания (см. `fetchAllFolders`): усечённый список ломал дерево, меню
 * строк и пикер перемещения для папок за отсечкой.
 *
 * Папку-корень ОБЛАСТИ по системному якорю тут не ищем — это забота
 * `resolveRootByCode` из `driveScope`: экранам нужны разные корни (весь Диск
 * против папки сущности), а загрузка у них одна и та же.
 *
 * @example
 * ```tsx
 * const { folders, foldersById, rootFolder, loading, error, reload } =
 *   useDriveFolders();
 * ```
 */
export interface UseDriveFoldersOptions {
  /**
   * Якорь области: грузить ТОЛЬКО её поддерево (+предков для прав) вместо
   * всего Диска. Для встраиваемых панелей больших инсталляций.
   */
  scopeCode?: string;
}

export function useDriveFolders(options: UseDriveFoldersOptions = {}): UseDriveFoldersResult {
  const { scopeCode } = options;
  const folderRepo = useFolderRepository();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Живой ли компонент. Диск часто открыт в модалке (пикер, поиск), которую
   * закрывают, не дождавшись ответа — без этой проверки ответ прилетал бы в
   * состояние уже размонтированного компонента.
   */
  const mounted = useRef(true);
  /** Номер последнего запроса — отсекает ответы устаревших загрузок. */
  const requestGeneration = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    // Номер запроса: при быстрой смене области (переключение карточек) ответы
    // могут прийти в обратном порядке, и старый scope перетёр бы новый —
    // применяем только результат ПОСЛЕДНЕГО запроса (код-ревью 2026-09-01).
    const generation = ++requestGeneration.current;
    const isStale = () => !mounted.current || generation !== requestGeneration.current;
    try {
      setError(null);
      setLoading(true);
      const data = scopeCode
        ? await fetchScopeFolders(folderRepo, scopeCode)
        : await fetchAllFolders(folderRepo);
      if (!isStale()) setFolders(data);
    } catch (err) {
      logger.error('[useDriveFolders] Ошибка загрузки папок:', err);
      if (!isStale()) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки Диска');
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
    // Прокси репозитория пересоздаётся на каждый рендер — в зависимости не включаем,
    // иначе reload менял бы идентичность и перезапускал эффект бесконечно.
  }, [scopeCode]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const foldersById = useMemo(() => new Map(folders.map(folder => [folder.id, folder])), [folders]);
  const rootFolder = useMemo(() => folders.find(folder => folder.parent_id == null), [folders]);
  return { folders, foldersById, rootFolder, loading, error, reload };
}
